import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import AppToolTip from '../InputBox/AppToolTip';
import { font } from '../../Tokens/Typography';
import { borderRadius } from '../../Tokens/Scales';

/**
 * AppButton — standard labelled button for the IMA360 Design System.
 *
 * Use this for all "Save", "Cancel", "Submit" and action buttons.
 * For icon-only buttons use AppIconButton instead.
 *
 * @param {React.ReactNode} children   Button label (required)
 * @param {function}        onClick
 * @param {'text'|'outlined'|'contained'} variant   (default 'outlined')
 * @param {string}          color      MUI palette key (default 'primary')
 * @param {'small'|'medium'|'large'}  size  (default 'small')
 * @param {boolean}         disabled
 * @param {boolean}         loading    Shows spinner and disables button
 * @param {React.ReactNode} tooltip    Tooltip text shown on hover
 * @param {React.ReactNode} startIcon  Icon before label
 * @param {React.ReactNode} endIcon    Icon after label
 * @param {boolean}         fullWidth
 * @param {'button'|'submit'|'reset'}  type  (default 'button')
 * @param {object}          sx         Extra sx on the root Button
 */
const AppButton = ({
  children,
  onClick,
  variant = 'outlined',
  color = 'primary',
  size = 'small',
  disabled = false,
  loading = false,
  tooltip = '',
  startIcon,
  endIcon,
  fullWidth = false,
  type = 'button',
  sx,
  ...props
}) => {
  const button = (
    <MuiButton
      onClick={onClick}
      variant={variant}
      color={color}
      size={size}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : startIcon}
      endIcon={loading ? undefined : endIcon}
      fullWidth={fullWidth}
      type={type}
      sx={[
        {
          ...font.button,
          borderRadius: borderRadius.xs,
          textTransform: 'none',
          minWidth: 80,
          transition: 'all 0.2s ease',
          '&:hover:not(:disabled)': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
          },
        },
        sx,
      ]}
      {...props}
    >
      {children}
    </MuiButton>
  );

  return (
    <AppToolTip title={tooltip}>
      <span style={{ display: fullWidth ? 'block' : 'inline-flex' }}>{button}</span>
    </AppToolTip>
  );
};

AppButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  color: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  tooltip: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default memo(AppButton);
